/**
 * What a recipient needs before the ciphertext arrives: the keys from the link and the password,
 * and the metadata. And the decryption of the download itself, which checks the stream against
 * the authenticated metadata.
 */
import { applyPasswordProtection, computeAuthToken, createDecryptStream, decryptMetadata, deriveKeyFromPassword, deriveKeys, expectedBaseNonce, expectedPlaintextSize, fromBase64url, toBase64url, } from "@skysend/crypto";
/** Derives the file, metadata and auth keys from an already recovered secret. */
export async function keysFromSecret(secret, saltB64) {
    const salt = fromBase64url(saltB64);
    const keys = await deriveKeys(secret, salt);
    const authToken = toBase64url(await computeAuthToken(keys.authKey));
    return { secret, salt, keys, authToken };
}
/**
 * Recovers the secret from the link fragment, taking the password protection off when the
 * share has one, and derives the keys from it.
 */
export async function keysFromLink(secretB64, saltB64, password) {
    let secret = fromBase64url(secretB64);
    if (password?.password) {
        if (!password.saltB64)
            throw new Error("Missing password salt");
        const { key } = await deriveKeyFromPassword(password.password, fromBase64url(password.saltB64), password.argon2id);
        secret = applyPasswordProtection(secret, key);
    }
    return keysFromSecret(secret, saltB64);
}
/** Bytes of standard base64, the way the server keeps the metadata of an upload. */
export function fromBase64(text) {
    const binary = atob(text);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
/** Decrypts the metadata of an upload. Null for an upload that carries none. */
export async function decryptUploadMetadata(info, metaKey) {
    if (!info.encryptedMeta || !info.nonce)
        return null;
    return decryptMetadata(fromBase64(info.encryptedMeta), fromBase64(info.nonce), metaKey);
}
/**
 * The plaintext of a download. It fails on a stream the server cut short, by the size in the
 * metadata, and on records the server reordered, by the base nonce the metadata names. An upload
 * of an older client without a base nonce decrypts without that check.
 */
export function decryptedStream(ciphertext, metadata, fileKey) {
    return ciphertext.pipeThrough(createDecryptStream(fileKey, expectedPlaintextSize(metadata), expectedBaseNonce(metadata)));
}
//# sourceMappingURL=download.js.map